import { useNavigate, useSearch } from '@tanstack/react-router'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { UsersRound } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { session } from '@/api/session'
import type { GroupSummary } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { groupStatusLabel } from '@/features/groups/constants'
import { GroupFilters } from '@/features/groups/group-filters'
import { HealthIndicator, RoundEligibility } from '@/features/groups/health'
import { describeHealth } from '@/features/groups/health-presentation'
import { NewGroupDialog } from '@/features/groups/new-group-dialog'
import { useGroupPermissions } from '@/features/groups/permissions'
import { useGroupList } from '@/features/groups/queries'
import { formatShortDate } from '@/lib/datetime'

import { DEFAULT_PAGE_SIZE, type GroupListSearch } from './search'

/**
 * The group register.
 *
 * The column this screen exists for is health. A branch manager scans it to decide who can be
 * considered for another round, and the failure mode is not a wrong number — it is a group with no
 * number at all being read as though it had a bad one, or worse, a good one. So the health cell
 * never renders blank and never renders a zero it was not given: an unscored group says so, in a
 * tone that is neither the green of a pass nor the red of a failure, with a dashed edge that
 * survives greyscale.
 */

/**
 * Nothing here is sortable. The list endpoint takes no ordering parameter, and ordering the page
 * that happens to be in the browser would be a claim about the whole result set that the next page
 * would immediately contradict.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<GroupSummary>()

const columns: DataTableColumn<GroupSummary>[] = helper.columns([
  helper.accessor('groupCode', {
    header: 'Code',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.groupCode}</span>,
    // On a card the code sits under the name: a group is known to an officer by its name, and the
    // code is what confirms it.
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('name', {
    header: 'Group',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.name}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('branchId', {
    header: 'Branch',
    enableSorting: false,
    cell: (context) => <span className="text-body">{context.row.original.branchId}</span>,
    meta: { noWrap: true, widthClass: 'w-24' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
  helper.accessor('loanOfficerName', {
    header: 'Officer',
    enableSorting: false,
    cell: (context) =>
      context.row.original.loanOfficerName ?? (
        // Not a dash. An unassigned group is a group nobody is answerable for, which is a finding
        // rather than a gap in the data.
        <span className="text-warning-text">Unassigned</span>
      ),
    meta: { widthClass: 'w-40' },
  }),
  helper.accessor('activeMemberCount', {
    header: 'Members',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric tabular-nums">{context.row.original.activeMemberCount}</span>
    ),
    meta: { align: 'right', widthClass: 'w-24' },
  }),
  helper.accessor('healthScore', {
    header: 'Health',
    enableSorting: false,
    cell: (context) => <HealthIndicator health={describeHealth(context.row.original)} />,
    meta: { widthClass: 'w-36', cardLabel: 'Health' },
  }),
  helper.accessor('healthQualifies', {
    header: 'Another round',
    enableSorting: false,
    cell: (context) => <RoundEligibility health={describeHealth(context.row.original)} />,
    meta: { widthClass: 'w-40', cardLabel: 'Another round' },
  }),
  helper.accessor('nextMeetingDate', {
    header: 'Next meeting',
    enableSorting: false,
    cell: (context) =>
      context.row.original.nextMeetingDate ? (
        <span className="numeric tabular-nums">
          {formatShortDate(context.row.original.nextMeetingDate)}
        </span>
      ) : (
        <span className="text-muted">Nothing scheduled</span>
      ),
    meta: { noWrap: true, widthClass: 'w-36' },
  }),
])

/** Long enough that a name typed at speed is one request, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300

export function GroupListPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/authenticated/groups' })
  const permissions = useGroupPermissions()
  const { user } = useAuth()

  const [searchText, setSearchText] = useState(search.search ?? '')
  const [creating, setCreating] = useState(false)

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE
  const workingBranchId = session.getBranchId() ?? user?.homeBranchId ?? null

  /**
   * Every change to the list's state goes through here, spelled out member by member rather than
   * spread from whatever the previous route carried. A filter left behind by another screen is
   * exactly the kind of thing that makes a list quietly show the wrong rows.
   */
  const updateSearch = (patch: Partial<GroupListSearch>, replace = false) => {
    void navigate({
      to: '/groups',
      search: {
        search: search.search,
        status: search.status,
        branchId: search.branchId,
        loanOfficerId: search.loanOfficerId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    })
  }

  // What this screen last wrote into the URL. Comparing against it is how a Back button, or a
  // pasted link, gets mirrored into the box without the debounce writing it straight back out.
  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      void navigate({
        to: '/groups',
        search: {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          branchId: search.branchId,
          loanOfficerId: search.loanOfficerId,
          page: undefined,
          pageSize: search.pageSize,
        },
        // Typing produces one history entry for the search, not one per keystroke.
        replace: true,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
    // Every filter is a dependency, not only the search text: changing one mid-keystroke restarts
    // the debounce against the new values, so a timer holding stale filters cannot land 300ms
    // later and undo the change the operator just made.
  }, [
    searchText,
    search.search,
    search.status,
    search.branchId,
    search.loanOfficerId,
    search.pageSize,
    navigate,
  ])

  const query = useGroupList({
    search: search.search,
    status: search.status,
    branchId: search.branchId,
    loanOfficerId: search.loanOfficerId,
    page,
    pageSize,
  })

  const openGroup = (group: GroupSummary) => {
    void navigate({ to: '/groups/$groupId', params: { groupId: group.id } })
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(search.search ?? search.status ?? search.loanOfficerId)

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    void navigate({ to: '/groups', search: { pageSize: search.pageSize } })
  }

  const describeFilters = () => {
    const parts: string[] = []
    if (search.search) {
      parts.push(`matching “${search.search}”`)
    }
    if (search.status) {
      parts.push(`with status ${groupStatusLabel(search.status).toLowerCase()}`)
    }
    if (search.loanOfficerId) {
      parts.push('for the selected officer')
    }

    return parts.join(', ')
  }

  return (
    <>
      <PageHeader
        title="Groups"
        description="Village banking. A round is granted to a group, so the two facts on every row are who is in it and whether it is healthy enough for another one."
        actions={
          permissions.canCreate ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <UsersRound />
              Form a group
            </Button>
          ) : null
        }
      />

      <div className="space-y-3">
        <GroupFilters
          searchText={searchText}
          onSearchTextChange={setSearchText}
          status={search.status}
          onStatusChange={(status) => {
            updateSearch({ status, page: undefined })
          }}
          loanOfficerId={search.loanOfficerId}
          onLoanOfficerChange={(loanOfficerId) => {
            updateSearch({ loanOfficerId, page: undefined })
          }}
          branchId={workingBranchId}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={openGroup}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
          emptyTitle={hasActiveFilters ? 'No groups match' : 'No groups yet'}
          emptyDescription={
            hasActiveFilters
              ? `Nothing was found ${describeFilters()}.`
              : 'No groups have been formed at the branches you can see. A group starts in formation and becomes active once it has a leader.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear the filters
              </Button>
            ) : permissions.canCreate ? (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
                <UsersRound />
                Form a group
              </Button>
            ) : null
          }
        />
      </div>

      <NewGroupDialog branchId={workingBranchId} open={creating} onOpenChange={setCreating} />
    </>
  )
}
